/**
 * Push notifications — composer (left: form, right: live iPhone preview).
 * Used for new campaigns, editing drafts / paused campaigns, and editing the
 * automatic (triggered) templates.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pushAPI, errMsg } from '../services/pushApi';
import {
  ACTIONS, APP_ROUTES, CATEGORIES, COMMON_VARS, KIND_LABELS, LINK_VARS, PRIORITIES, S, SOUNDS,
  TIMEZONES, TRIGGERS, WEEKDAYS, EngineErrorBanner, StatusPill, describeAudience, describeSchedule, fillVars, testSampleVars,
} from './pushShared';

const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; } })();

export const emptyCampaign = () => ({
  id: null,
  name: '',
  kind: 'instant',
  status: 'draft',
  category: 'updates',
  title: '',
  subtitle: '',
  body: '',
  image_url: '',
  sound: 'default',
  badge: 'none',
  badge_value: '',
  deep_link: '',
  actions_json: [],
  thread_id: '',
  collapse_id: '',
  interruption: 'active',
  ttl_hours: '',
  audience_json: { mode: 'segment', segment: {}, exclude_user_ids: [], exclude_paid: false },
  send_at: '',
  local_time: false,
  timezone: browserTz,
  recurrence_json: { freq: 'weekly', time: '09:00', weekdays: [1] },
  trigger_key: null,
  trigger_offset_json: null,
  respect_quiet_hours: true,
});

// API row → composer state
function fromRow(r) {
  const base = emptyCampaign();
  const c = { ...base };
  for (const k of Object.keys(base)) if (r[k] !== undefined && r[k] !== null) c[k] = r[k];
  c.send_at = r.send_at_wall || '';
  c.actions_json = Array.isArray(r.actions_json) ? r.actions_json : [];
  c.audience_json = r.audience_json || base.audience_json;
  c.recurrence_json = r.recurrence_json || base.recurrence_json;
  c.badge_value = r.badge_value == null ? '' : r.badge_value;
  c.ttl_hours = r.ttl_hours == null ? '' : r.ttl_hours;
  c.timezone = r.timezone || base.timezone;
  return c;
}

// composer state → API body
function toBody(c) {
  const b = {
    name: c.name, kind: c.kind, category: c.category, title: c.title, subtitle: c.subtitle, body: c.body,
    image_url: c.image_url, sound: c.sound, badge: c.badge,
    badge_value: c.badge === 'set' ? (c.badge_value === '' ? null : Number(c.badge_value)) : null,
    deep_link: c.deep_link, actions_json: c.actions_json.length ? c.actions_json : null,
    thread_id: c.thread_id, collapse_id: c.collapse_id, interruption: c.interruption,
    ttl_hours: c.ttl_hours === '' ? null : Number(c.ttl_hours),
    respect_quiet_hours: !!c.respect_quiet_hours,
    local_time: c.kind === 'instant' ? false : !!c.local_time,
    timezone: c.timezone || null,
  };
  if (c.kind !== 'triggered') b.audience_json = cleanAudience(c.audience_json);
  if (c.kind === 'scheduled') b.send_at = c.send_at;
  if (c.kind === 'recurring') b.recurrence_json = cleanRecurrence(c.recurrence_json);
  if (c.kind === 'triggered') b.trigger_offset_json = c.trigger_key === 'filing.due' ? c.trigger_offset_json : null;
  return b;
}

function cleanAudience(a) {
  const out = { mode: a.mode, exclude_user_ids: a.exclude_user_ids || [], exclude_paid: !!a.exclude_paid };
  if (a.mode === 'segment') {
    const seg = {};
    for (const [k, v] of Object.entries(a.segment || {})) {
      if (v === '' || v == null || (Array.isArray(v) && !v.length)) continue;
      seg[k] = v;
    }
    out.segment = seg;
  }
  if (a.mode === 'users' || a.mode === 'test') { out.user_ids = a.user_ids || []; out.emails = a.emails || []; }
  return out;
}

function cleanRecurrence(r) {
  const out = { freq: r.freq, time: r.time };
  if (r.freq === 'weekly') out.weekdays = r.weekdays || [];
  if (r.freq === 'monthly') out.monthday = Number(r.monthday) || null;
  if (r.ends_at) out.ends_at = r.ends_at;
  if (r.max_sends) out.max_sends = Number(r.max_sends);
  return out;
}

// "117, a@b.com\n120" → { user_ids, emails, bad }
function parseIdsEmails(text) {
  const user_ids = []; const emails = []; const bad = [];
  for (const tok of String(text || '').split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean)) {
    if (/^\d+$/.test(tok)) { const n = Number(tok); if (!user_ids.includes(n)) user_ids.push(n); }
    else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tok)) { const e = tok.toLowerCase(); if (!emails.includes(e)) emails.push(e); }
    else bad.push(tok);
  }
  return { user_ids, emails, bad };
}

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result));
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function PushComposer({ initial, role, onClose, onSaved, flash }) {
  const [c, setC] = useState(() => (initial ? fromRow(initial) : emptyCampaign()));
  const [dirty, setDirty] = useState(!initial);
  const [busy, setBusy] = useState('');
  const [errors, setErrors] = useState([]);
  const [count, setCount] = useState(null);
  const [countErr, setCountErr] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [offWarning, setOffWarning] = useState(false);
  const [usersText, setUsersText] = useState(() => {
    const a = initial?.audience_json;
    return a ? [...(a.user_ids || []), ...(a.emails || [])].join(', ') : '';
  });
  const [excludeText, setExcludeText] = useState(() => (initial?.audience_json?.exclude_user_ids || []).join(', '));
  const [linkCustom, setLinkCustom] = useState(() => !!initial?.deep_link && !APP_ROUTES.some((r) => r.path === initial.deep_link));
  const focusRef = useRef('body');
  const fieldRefs = { title: useRef(null), subtitle: useRef(null), body: useRef(null) };

  const isTriggered = c.kind === 'triggered';
  const editable = isTriggered ? c.status !== 'cancelled' : ['draft', 'paused'].includes(c.status);
  const set = (patch) => { setC((p) => ({ ...p, ...patch })); setDirty(true); };
  const setAud = (patch) => set({ audience_json: { ...c.audience_json, ...patch } });
  const setSeg = (k, v) => setAud({ segment: { ...(c.audience_json.segment || {}), [k]: v } });
  const setRec = (patch) => set({ recurrence_json: { ...c.recurrence_json, ...patch } });

  const insertVar = (v) => {
    const key = focusRef.current;
    const el = fieldRefs[key]?.current;
    const token = `{{${v}}}`;
    const cur = String(c[key] || '');
    const start = el ? el.selectionStart ?? cur.length : cur.length;
    const end = el ? el.selectionEnd ?? cur.length : cur.length;
    set({ [key]: cur.slice(0, start) + token + cur.slice(end) });
    setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(start + token.length, start + token.length); } }, 0);
  };

  // ── live audience count (debounced) ──
  const audienceKey = JSON.stringify([cleanAudience(c.audience_json), c.category]);
  useEffect(() => {
    if (isTriggered) return undefined;
    setCountErr('');
    const t = setTimeout(async () => {
      try {
        const r = await pushAPI.audienceCount({ audience_json: cleanAudience(c.audience_json), category: c.category });
        setCount(r.data);
      } catch (e) { setCount(null); setCountErr(errMsg(e, 'Could not count the audience')); }
    }, 700);
    return () => clearTimeout(t);
  }, [audienceKey, isTriggered]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useCallback(async ({ quiet } = {}) => {
    setBusy('save'); setErrors([]);
    try {
      const body = toBody(c);
      const r = c.id ? await pushAPI.update(c.id, body) : await pushAPI.create(body);
      const item = r.data.item;
      setC(fromRow(item)); setDirty(false);
      onSaved && onSaved(item);
      if (!quiet) flash('Saved');
      return item;
    } catch (e) {
      setErrors(e?.response?.data?.errors || [errMsg(e, 'Save failed')]);
      return null;
    } finally { setBusy(''); }
  }, [c, flash, onSaved]);

  const sendTest = async ({ toMe, text }) => {
    const parsed = parseIdsEmails(text);
    if (!toMe && parsed.bad.length) { flash(`Not an id or email: ${parsed.bad[0]}`); return; }
    if (!toMe && !parsed.user_ids.length && !parsed.emails.length) { flash('Add a member id or email'); return; }
    setBusy('test');
    try {
      const target = { ...(toMe ? { to_me: true } : { user_ids: parsed.user_ids, emails: parsed.emails }), sample_vars: testSampleVars() };
      const r = c.id && !dirty
        ? await pushAPI.testCampaign(c.id, target)
        : await pushAPI.testDraft({ draft: toBody({ ...c, name: c.name || 'Test' }), ...target });
      const res = r.data.result || {};
      const devices = Array.isArray(res.results) ? res.results : Array.isArray(res.devices) ? res.devices : null;
      const sent = devices ? devices.filter((d) => d.status === 'sent').length : null;
      const noApp = devices ? devices.filter((d) => d.status === 'no_device').length : 0;
      const failed = devices ? devices.filter((d) => d.status === 'failed') : [];
      flash(!devices ? 'Test sent'
        : sent === devices.length ? `Test sent to ${sent} device${sent === 1 ? '' : 's'}`
        : `Test: ${sent} sent${noApp ? `, ${noApp} member${noApp === 1 ? ' has' : 's have'} no app installed` : ''}${failed.length ? `, ${failed.length} failed (${failed[0].reason || 'error'})` : ''}`);
      setTestOpen(false);
    } catch (e) { flash(errMsg(e, 'Test failed')); }
    finally { setBusy(''); }
  };

  const startFlow = async () => {
    let item = c;
    if (dirty || !c.id) { item = await save({ quiet: true }); if (!item) return; }
    let members = null;
    let needsConfirm = false;
    if (!isTriggered) {
      try {
        const r = await pushAPI.approval(item.id);
        members = r.data; needsConfirm = !!r.data.needs_confirm;
        if (!r.data.can_approve) { flash(`This reaches ${r.data.members} members, which counts as a large send. Only a super admin can approve it.`); return; }
      } catch (e) { flash(errMsg(e, 'Could not count the audience')); return; }
    }
    setConfirm({ item, isAll: needsConfirm, members, typed: '' });
  };

  const doStart = async () => {
    const { item, isAll, members, typed } = confirm;
    setBusy('start');
    try {
      const body = isAll ? { confirm_count: Number(typed) } : {};
      const r = item.status === 'paused' && !isTriggered ? await pushAPI.resume(item.id, body) : await pushAPI.approve(item.id, body);
      setC(fromRow(r.data.item)); setDirty(false); setConfirm(null);
      onSaved && onSaved(r.data.item);
      if (r.data.engine && r.data.engine.push_enabled === false) {
        setOffWarning(true);
      } else {
        flash(isTriggered ? 'Template is live' : item.kind === 'instant' ? `Sending to ${members?.members ?? ''} members` : 'Approved and scheduled');
      }
    } catch (e) {
      const msg = errMsg(e, 'Could not start');
      const late = /pick a new time|in the past/i.test(msg);
      setConfirm((p) => (p ? { ...p, error: late
        ? 'The send time has already passed, so it can’t go out. Close this, pick a new date and time under “When”, save, then approve again.'
        : msg } : p));
    }
    finally { setBusy(''); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) { flash('Use a JPG, PNG or GIF'); return; }
    if (file.size > 1024 * 1024) { flash('Image is over 1 MB. Make it smaller first.'); return; }
    setBusy('upload');
    try {
      const fileBase64 = await fileToBase64(file);
      const r = await pushAPI.uploadImage({ fileBase64, fileName: file.name, mime: file.type });
      set({ image_url: r.data.url });
    } catch (e) { flash(errMsg(e, 'Upload failed')); }
    finally { setBusy(''); }
  };

  const vars = isTriggered ? [...(TRIGGERS[c.trigger_key]?.vars || []).filter((v) => !LINK_VARS.includes(v)), 'first_name', 'last_name'] : COMMON_VARS;
  const triggerLink = isTriggered ? TRIGGERS[c.trigger_key]?.link : null;
  const startLabel = isTriggered ? 'Turn on'
    : c.status === 'paused' ? 'Approve & resume'
    : c.kind === 'instant' ? 'Approve & send now'
    : c.kind === 'scheduled' ? 'Approve & schedule' : 'Approve & start';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button style={S.btnGhost} onClick={onClose}>← Back</button>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: '#111827' }}>
          {isTriggered ? `Automatic: ${TRIGGERS[c.trigger_key]?.label || c.trigger_key}` : c.id ? (c.name || 'Untitled') : 'New push notification'}
        </h2>
        {c.id && <StatusPill status={c.status} />}
        {dirty && c.id && <span style={{ fontSize: 12, color: '#B45309' }}>Unsaved changes</span>}
      </div>

      <EngineErrorBanner err={c.status === 'paused' ? initial?.engine_error : null} />
      {offWarning && (
        <div style={{ ...S.banner, background: '#FEE2E2', color: '#991B1B' }}>
          <b>Saved and approved, but sending is switched off on this server, so nothing will go out.</b>{' '}
          If it isn't switched on within 6 hours, you'll need to approve again.
        </div>
      )}
      {!editable && (
        <div style={S.banner}>This campaign is {c.status}. Pause it to make changes, or duplicate it to start a new one.</div>
      )}
      {errors.length > 0 && (
        <div style={{ ...S.banner, background: '#FEE2E2', color: '#991B1B' }}>
          <b>Please fix:</b>
          <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>{errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 20, alignItems: 'start' }}>
        <fieldset disabled={!editable || !!busy} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          {/* ── Basics ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Basics</div>
            <div style={S.sectionHelp}>The name is only for us. Members never see it.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label><span style={S.lbl}>Internal name</span>
                <input style={S.inp} value={c.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. October franchise-tax reminder" /></label>
              <label><span style={S.lbl}>Category</span>
                <select style={S.inp} value={c.category} onChange={(e) => set({ category: e.target.value })}>
                  {CATEGORIES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                </select></label>
            </div>
            <div style={S.help}>{CATEGORIES.find((x) => x.id === c.category)?.help}</div>
            {c.category === 'marketing' && (
              <div style={{ ...S.help, color: '#92400E' }}>Offers & news only reach members who opted in, and at most 2 per member per day.</div>
            )}
          </div>

          {/* ── Message ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Message</div>
            <div style={S.sectionHelp}>Click a variable to insert it where your cursor is. It is filled in for each member.</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {vars.map((v) => (
                <button type="button" key={v} style={S.chip} onMouseDown={(e) => e.preventDefault()} onClick={() => insertVar(v)}>{`{{${v}}}`}</button>
              ))}
            </div>
            <TextField label="Title" refEl={fieldRefs.title} value={c.title} visible={50} max={120}
              onFocus={() => { focusRef.current = 'title'; }} onChange={(v) => set({ title: v })} placeholder="Your franchise tax is due soon" />
            <TextField label="Subtitle (optional)" refEl={fieldRefs.subtitle} value={c.subtitle} visible={60} max={120}
              onFocus={() => { focusRef.current = 'subtitle'; }} onChange={(v) => set({ subtitle: v })} placeholder="Acme Labs Inc." />
            <TextField label="Message" multiline refEl={fieldRefs.body} value={c.body} visible={150} max={600}
              onFocus={() => { focusRef.current = 'body'; }} onChange={(v) => set({ body: v })}
              placeholder="Hi {{first_name}}, your Delaware franchise tax is due on {{due_date}}. Tap to pay in 2 minutes." />

            <div style={{ marginTop: 12 }}>
              <span style={S.lbl}>Image (optional)</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input style={S.inp} value={c.image_url} onChange={(e) => set({ image_url: e.target.value.trim() })} placeholder="https://… or upload" />
                <label style={{ ...S.btnGhost, whiteSpace: 'nowrap', display: 'inline-block' }}>
                  {busy === 'upload' ? 'Uploading…' : 'Upload'}
                  <input type="file" accept="image/jpeg,image/png,image/gif" style={{ display: 'none' }}
                    onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
                </label>
                {c.image_url && <button type="button" style={S.mini} onClick={() => set({ image_url: '' })}>Remove</button>}
              </div>
              <div style={S.help}>Shows when the member presses and holds the notification. JPG, PNG or GIF, under 1 MB. A wide image (2:1) looks best.</div>
            </div>
          </div>

          {/* ── On tap ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>When they tap it</div>
            <div style={S.sectionHelp}>Pick the screen in the app to open, or a web page.</div>
            <LinkPicker value={c.deep_link} custom={linkCustom} setCustom={setLinkCustom} onChange={(v) => set({ deep_link: v })} />
            {triggerLink && c.deep_link !== triggerLink && (
              <div style={S.help}>
                This trigger can open the exact screen it is about: <code>{triggerLink}</code>{' '}
                <button type="button" style={S.mini} onClick={() => { setLinkCustom(true); set({ deep_link: triggerLink }); }}>Use this link</button>
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              <span style={S.lbl}>Buttons (up to 2, shown when the member presses and holds)</span>
              {c.actions_json.map((a, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '180px 1fr auto', gap: 8, alignItems: 'start', marginBottom: 8 }}>
                  <select style={S.inp} value={a.id} onChange={(e) => {
                    const next = [...c.actions_json]; const id = e.target.value;
                    next[i] = id === 'later' ? { id, snooze_hours: 24 } : { id, deep_link: a.deep_link || '' };
                    set({ actions_json: next });
                  }}>
                    {ACTIONS.map((x) => (
                      <option key={x.id} value={x.id} disabled={x.id !== a.id && c.actions_json.some((y) => y.id === x.id)}>{x.title}</option>
                    ))}
                  </select>
                  {a.id === 'later' ? (
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: '#374151' }}>
                      Remind again after
                      <input style={{ ...S.inp, width: 70 }} type="number" min={1} max={168} value={a.snooze_hours ?? 24}
                        onChange={(e) => { const next = [...c.actions_json]; next[i] = { ...a, snooze_hours: e.target.value === '' ? '' : Number(e.target.value) }; set({ actions_json: next }); }} />
                      hours
                    </label>
                  ) : (
                    <LinkPicker compact value={a.deep_link || ''} custom={!!a.deep_link && !APP_ROUTES.some((r) => r.path === a.deep_link)}
                      onChange={(v) => { const next = [...c.actions_json]; next[i] = { ...a, deep_link: v }; set({ actions_json: next }); }} />
                  )}
                  <button type="button" style={S.mini} onClick={() => set({ actions_json: c.actions_json.filter((_, j) => j !== i) })}>Remove</button>
                </div>
              ))}
              {c.actions_json.length < 2 && (
                <button type="button" style={S.mini} onClick={() => {
                  const free = ACTIONS.find((x) => !c.actions_json.some((y) => y.id === x.id));
                  set({ actions_json: [...c.actions_json, free.id === 'later' ? { id: 'later', snooze_hours: 24 } : { id: free.id, deep_link: '' }] });
                }}>+ Add button</button>
              )}
              <div style={S.help}>Button labels are fixed by the app. A button with no link opens the same screen as tapping the notification.</div>
            </div>
          </div>

          {/* ── Delivery ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>How it arrives</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label><span style={S.lbl}>Sound</span>
                <select style={S.inp} value={c.sound} onChange={(e) => set({ sound: e.target.value })}>
                  {SOUNDS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                </select></label>
              <label><span style={S.lbl}>App icon badge</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select style={S.inp} value={c.badge} onChange={(e) => set({ badge: e.target.value })}>
                    <option value="none">Don't change</option>
                    <option value="increment">Add 1 to the red badge</option>
                    <option value="set">Set to a number</option>
                  </select>
                  {c.badge === 'set' && <input style={{ ...S.inp, width: 80 }} type="number" min={0} value={c.badge_value} onChange={(e) => set({ badge_value: e.target.value })} />}
                </div></label>
            </div>
            <div style={{ marginTop: 12 }}>
              <span style={S.lbl}>Priority</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {PRIORITIES.map((p) => (
                  <label key={p.id} style={{ border: `1px solid ${c.interruption === p.id ? '#2540D9' : '#E5E7EB'}`, borderRadius: 10, padding: 10, cursor: 'pointer', background: c.interruption === p.id ? '#EEF2FF' : '#fff' }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, fontWeight: 600, color: '#111827' }}>
                      <input type="radio" checked={c.interruption === p.id} onChange={() => set({ interruption: p.id })} /> {p.label}
                    </div>
                    <div style={{ ...S.help, marginTop: 4 }}>{p.help}</div>
                  </label>
                ))}
              </div>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontSize: 13, color: '#374151' }}>
              <input type="checkbox" checked={!!c.respect_quiet_hours} onChange={(e) => set({ respect_quiet_hours: e.target.checked })} />
              Respect quiet hours (hold until 8:00 if it would arrive between 22:00 and 8:00 the member's time)
            </label>
            <button type="button" style={{ ...S.mini, marginTop: 12 }} onClick={() => setShowAdvanced((v) => !v)}>
              {showAdvanced ? '▾' : '▸'} Advanced
            </button>
            {showAdvanced && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
                <label><span style={S.lbl}>Group id</span>
                  <input style={S.inp} value={c.thread_id} maxLength={64} onChange={(e) => set({ thread_id: e.target.value })} placeholder="e.g. filings" />
                  <div style={S.help}>Notifications with the same group id stack together on the lock screen.</div></label>
                <label><span style={S.lbl}>Replace id</span>
                  <input style={S.inp} value={c.collapse_id} maxLength={64} onChange={(e) => set({ collapse_id: e.target.value })} placeholder="e.g. oct-tax" />
                  <div style={S.help}>A new notification with the same replace id replaces the older one instead of adding another.</div></label>
                <label><span style={S.lbl}>Expires after (hours)</span>
                  <input style={S.inp} type="number" min={0} max={720} value={c.ttl_hours} onChange={(e) => set({ ttl_hours: e.target.value })} placeholder="Apple default" />
                  <div style={S.help}>If the phone is off longer than this, the notification is dropped. Leave empty for the default; 0 means now or never.</div></label>
              </div>
            )}
          </div>

          {/* ── Audience ── */}
          {!isTriggered && (
            <div style={S.section}>
              <div style={S.sectionTitle}>Who gets it</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {[['all', 'All members'], ['segment', 'A segment'], ['users', 'Specific members'], ['test', 'Test devices']].map(([m, l]) => (
                  <button type="button" key={m} onClick={() => setAud({ mode: m })}
                    style={{ ...S.mini, padding: '6px 12px', fontSize: 13, ...(c.audience_json.mode === m ? { background: '#2540D9', color: '#fff', borderColor: '#2540D9' } : {}) }}>{l}</button>
                ))}
              </div>
              {c.audience_json.mode === 'all' && (
                <div style={S.help}>Everyone with a Naytive account. Only a super admin can send this, and they will be asked to type the member count to confirm.{role !== 'super_admin' && <b style={{ color: '#B91C1C' }}> You can save it as a draft, but a super admin has to approve it.</b>}</div>
              )}
              {c.audience_json.mode === 'segment' && <SegmentBuilder seg={c.audience_json.segment || {}} setSeg={setSeg} />}
              {(c.audience_json.mode === 'users' || c.audience_json.mode === 'test') && (
                <div>
                  <span style={S.lbl}>{c.audience_json.mode === 'test' ? 'Test members (ids or emails)' : 'Member ids or emails'}</span>
                  <textarea style={{ ...S.inp, minHeight: 70 }} value={usersText}
                    onChange={(e) => { setUsersText(e.target.value); const p = parseIdsEmails(e.target.value); setAud({ user_ids: p.user_ids, emails: p.emails }); }}
                    placeholder="117, priya@example.com, 120 (paste from a spreadsheet: commas, spaces or new lines)" />
                  {(() => { const p = parseIdsEmails(usersText); return (
                    <div style={S.help}>{p.user_ids.length} id{p.user_ids.length === 1 ? '' : 's'}, {p.emails.length} email{p.emails.length === 1 ? '' : 's'}{p.bad.length > 0 && <span style={{ color: '#B91C1C' }}>. Ignored: {p.bad.slice(0, 3).join(', ')}{p.bad.length > 3 ? '…' : ''}</span>}</div>
                  ); })()}
                </div>
              )}
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #E5E7EB' }}>
                <span style={S.lbl}>Leave out</span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center' }}>
                  <input style={S.inp} value={excludeText} placeholder="Member ids to skip, e.g. 12, 40"
                    onChange={(e) => { setExcludeText(e.target.value); setAud({ exclude_user_ids: parseIdsEmails(e.target.value).user_ids }); }} />
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: '#374151', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={!!c.audience_json.exclude_paid} onChange={(e) => setAud({ exclude_paid: e.target.checked })} /> Skip paying members
                  </label>
                </div>
              </div>
              <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: '#F9FAFB', border: '1px solid #F3F4F6', fontSize: 13.5, color: '#111827' }}>
                {countErr ? <span style={{ color: '#B91C1C' }}>{countErr}</span>
                  : count ? (
                    <>
                      This will reach <b>{count.members.toLocaleString()}</b> member{count.members === 1 ? '' : 's'} (<b>{count.with_device.toLocaleString()}</b> with the app).
                      {count.opted_out > 0 && <span style={{ color: '#6B7280' }}> {count.opted_out} turned this category off.</span>}
                      <div style={{ ...S.help }}>Everyone also gets it in their in-app Activity feed. Only members with the app get the phone notification.</div>
                      {count.members >= 100 && <div style={{ ...S.help, color: '#92400E' }}>This is a large send (100 or more members), so a super admin must approve it and type the count.</div>}
                      {count.sample?.length > 0 && (
                        <div style={{ ...S.help }}>For example: {count.sample.slice(0, 5).map((s) => s.name || s.email || `#${s.id}`).join(', ')}</div>
                      )}
                    </>
                  ) : 'Counting…'}
              </div>
            </div>
          )}

          {/* ── Schedule ── */}
          <div style={S.section}>
            <div style={S.sectionTitle}>When</div>
            {isTriggered ? (
              <>
                <div style={S.help}><b>Sends automatically when:</b> {TRIGGERS[c.trigger_key]?.when}</div>
                {c.trigger_key === 'filing.due' && (
                  <label style={{ display: 'block', marginTop: 10 }}><span style={S.lbl}>Days before the due date</span>
                    <input style={{ ...S.inp, width: 200 }} defaultValue={(c.trigger_offset_json?.days_before || [7, 1]).join(', ')}
                      onChange={(e) => {
                        const days = e.target.value.split(/[\s,]+/).filter(Boolean).map(Number).filter((n) => Number.isInteger(n));
                        set({ trigger_offset_json: { days_before: days } });
                      }} />
                    <div style={S.help}>Comma-separated, e.g. 7, 1. Overdue filings are always reminded.</div></label>
                )}
              </>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                  {['instant', 'scheduled', 'recurring'].map((k) => (
                    <button type="button" key={k} onClick={() => set({ kind: k })}
                      style={{ ...S.mini, padding: '6px 12px', fontSize: 13, ...(c.kind === k ? { background: '#2540D9', color: '#fff', borderColor: '#2540D9' } : {}) }}>
                      {k === 'instant' ? 'Send now' : k === 'scheduled' ? 'At a date and time' : 'Repeating'}
                    </button>
                  ))}
                </div>
                {c.kind === 'instant' && <div style={S.help}>Goes out as soon as it is approved. Members in quiet hours get it at 8:00 their time (unless it is time-sensitive).</div>}
                {c.kind !== 'instant' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {c.kind === 'scheduled' ? (
                      <label><span style={S.lbl}>Date and time</span>
                        <input style={S.inp} type="datetime-local" value={c.send_at} onChange={(e) => set({ send_at: e.target.value })} /></label>
                    ) : (
                      <label><span style={S.lbl}>At</span>
                        <input style={S.inp} type="time" value={c.recurrence_json.time || ''} onChange={(e) => setRec({ time: e.target.value })} /></label>
                    )}
                    <label><span style={S.lbl}>Time zone</span>
                      <select style={S.inp} value={c.timezone || ''} disabled={c.local_time} onChange={(e) => set({ timezone: e.target.value })}>
                        {TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
                      </select></label>
                    <label style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: '#374151' }}>
                      <input type="checkbox" checked={!!c.local_time} onChange={(e) => set({ local_time: e.target.checked })} />
                      Use each member's local time (e.g. 9:00 in Dubai for Dubai members, 9:00 in London for London members)
                    </label>
                  </div>
                )}
                {c.kind === 'recurring' && (
                  <div style={{ marginTop: 12 }}>
                    <span style={S.lbl}>Repeat</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <select style={{ ...S.inp, width: 140 }} value={c.recurrence_json.freq} onChange={(e) => setRec({ freq: e.target.value })}>
                        <option value="daily">Every day</option>
                        <option value="weekly">Every week</option>
                        <option value="monthly">Every month</option>
                      </select>
                      {c.recurrence_json.freq === 'weekly' && WEEKDAYS.map((d) => {
                        const on = (c.recurrence_json.weekdays || []).includes(d.n);
                        return (
                          <button type="button" key={d.n} onClick={() => setRec({ weekdays: on ? c.recurrence_json.weekdays.filter((x) => x !== d.n) : [...(c.recurrence_json.weekdays || []), d.n].sort() })}
                            style={{ ...S.mini, ...(on ? { background: '#2540D9', color: '#fff', borderColor: '#2540D9' } : {}) }}>{d.l}</button>
                        );
                      })}
                      {c.recurrence_json.freq === 'monthly' && (
                        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>on day
                          <input style={{ ...S.inp, width: 70 }} type="number" min={1} max={28} value={c.recurrence_json.monthday || ''} onChange={(e) => setRec({ monthday: e.target.value })} />
                          <span style={S.help}>(1-28, so it exists in every month)</span></label>
                      )}
                    </div>
                    <span style={{ ...S.lbl, marginTop: 10 }}>Stop</span>
                    <EndPicker rec={c.recurrence_json} setRec={setRec} />
                  </div>
                )}
              </>
            )}
          </div>
        </fieldset>

        {/* ── Preview ── */}
        <div style={{ position: 'sticky', top: 16 }}>
          <PhonePreview c={c} />
        </div>
      </div>

      {/* ── Action bar ── */}
      <div style={{ position: 'sticky', bottom: 0, background: 'rgba(255,255,255,0.96)', borderTop: '1px solid #E5E7EB', padding: '12px 0', marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', zIndex: 5 }}>
        <span style={{ fontSize: 12.5, color: '#6B7280', marginRight: 'auto' }}>
          {KIND_LABELS[c.kind]} · {describeSchedule({ ...c, send_at_wall: c.send_at })} · {isTriggered ? 'the member the event is about' : describeAudience(c.audience_json)}
        </span>
        <button style={S.btnGhost} disabled={!!busy} onClick={() => setTestOpen(true)}>Send a test</button>
        {editable && <button style={S.btnGhost} disabled={!!busy || (!dirty && c.id)} onClick={() => save()}>{busy === 'save' ? 'Saving…' : isTriggered ? 'Save template' : 'Save draft'}</button>}
        {editable && !(isTriggered && c.status === 'active') && (
          <button style={S.btnPrimary} disabled={!!busy} onClick={startFlow}>{busy === 'start' ? 'Starting…' : startLabel}</button>
        )}
      </div>

      {testOpen && <TestDialog busy={busy === 'test'} onClose={() => setTestOpen(false)} onSend={sendTest} />}

      {confirm && (
        <div style={S.overlay} onClick={() => busy !== 'start' && setConfirm(null)}>
          <div style={{ ...S.modal, width: 'min(480px,100%)' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 10px' }}>{startLabel}?</h3>
            <div style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.55 }}>
              {isTriggered ? (
                <>From now on, every time this happens: <b>{TRIGGERS[c.trigger_key]?.when}</b> the member gets this notification.</>
              ) : (
                <>
                  <div><b>{confirm.item.title}</b></div>
                  <div style={{ marginTop: 6 }}>Goes to <b>{confirm.members?.members?.toLocaleString() ?? '?'}</b> members ({confirm.members?.with_device?.toLocaleString() ?? '?'} with the app).</div>
                  <div>When: {describeSchedule(confirm.item)}</div>
                  <div>Category: {CATEGORIES.find((x) => x.id === confirm.item.category)?.label}</div>
                </>
              )}
            </div>
            {confirm.isAll && (
              <div style={{ marginTop: 14 }}>
                <div style={{ ...S.banner, marginBottom: 8 }}>This is a large send (<b>{confirm.members?.members}</b> of {confirm.members?.total_members} members). Type <b>{confirm.members?.members}</b> to confirm.</div>
                <input style={S.inp} autoFocus value={confirm.typed} onChange={(e) => setConfirm((p) => ({ ...p, typed: e.target.value.trim() }))} placeholder="Member count" />
              </div>
            )}
            {confirm.error && (
              <div style={{ ...S.banner, background: '#FEE2E2', color: '#991B1B', marginTop: 14, marginBottom: 0 }}>{confirm.error}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={S.btnGhost} onClick={() => setConfirm(null)} disabled={busy === 'start'}>Cancel</button>
              <button style={S.btnPrimary} onClick={doStart}
                disabled={busy === 'start' || (confirm.isAll && String(confirm.members?.members) !== confirm.typed)}>
                {busy === 'start' ? 'Starting…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, onFocus, visible, max, multiline, refEl, placeholder }) {
  const n = String(value || '').length;
  const over = n > visible;
  const El = multiline ? 'textarea' : 'input';
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <span style={{ ...S.lbl, display: 'flex', justifyContent: 'space-between' }}>
        <span>{label}</span>
        <span style={{ color: n > max ? '#B91C1C' : over ? '#B45309' : '#9CA3AF' }}>
          {n} / {visible} visible{max !== visible ? ` · ${max} max` : ''}
        </span>
      </span>
      <El ref={refEl} style={{ ...S.inp, ...(multiline ? { minHeight: 90, resize: 'vertical' } : {}) }} value={value || ''}
        maxLength={max} onFocus={onFocus} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      {over && n <= max && <div style={{ ...S.help, color: '#B45309' }}>Past {visible} characters the lock screen cuts it off; the rest shows when the member opens it.</div>}
    </label>
  );
}

function LinkPicker({ value, onChange, custom, setCustom, compact }) {
  const [isCustom, setIsCustom] = useState(custom);
  const toggle = (v) => { setIsCustom(v); setCustom && setCustom(v); };
  const sel = isCustom ? '__custom' : value || '';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isCustom ? (compact ? '1fr 1fr' : '260px 1fr') : '1fr', gap: 8 }}>
      <select style={S.inp} value={sel} onChange={(e) => {
        const r = APP_ROUTES.find((x) => x.path === e.target.value);
        if (e.target.value === '__custom') { toggle(true); onChange(''); }
        // A screen that needs an id: filled in with its <placeholder> to replace.
        else if (r && r.param) { toggle(true); onChange(r.path); }
        else { toggle(false); onChange(e.target.value); }
      }}>
        <option value="">{compact ? 'Same as tapping' : 'Just open the app'}</option>
        {APP_ROUTES.map((r) => <option key={r.path} value={r.path}>{r.label} ({r.path})</option>)}
        <option value="__custom">Custom link…</option>
      </select>
      {isCustom && (
        <input style={S.inp} value={value} onChange={(e) => onChange(e.target.value.trim())} placeholder="/obligation/123 or https://naytive.com/…" />
      )}
      {isCustom && /<[^>]*>/.test(value || '') && (
        <div style={{ ...S.help, color: '#B45309', gridColumn: '1 / -1' }}>Replace the part in &lt;angle brackets&gt; with the real id.</div>
      )}
    </div>
  );
}

function SegmentBuilder({ seg, setSeg }) {
  const codes = (k) => (seg[k] || []).join(', ');
  const setCodes = (k, v) => setSeg(k, v.split(/[\s,]+/).map((x) => x.trim().toUpperCase()).filter(Boolean));
  const tri = (k) => (seg[k] === true ? 'yes' : seg[k] === false ? 'no' : '');
  const setTri = (k, v) => setSeg(k, v === 'yes' ? true : v === 'no' ? false : null);
  const num = (k, v) => setSeg(k, v === '' ? null : Number(v));
  const VISA = ['submitted', 'in_review', 'documents_required', 'processing', 'approved', 'rejected'];
  return (
    <div>
      <div style={S.help}>Members must match every filter you fill in. Empty filters are ignored.</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
        <label><span style={S.lbl}>Nationality (country codes)</span>
          <input style={S.inp} defaultValue={codes('countries')} onChange={(e) => setCodes('countries', e.target.value)} placeholder="IN, AE" /></label>
        <label><span style={S.lbl}>Lives in (country codes)</span>
          <input style={S.inp} defaultValue={codes('residency')} onChange={(e) => setCodes('residency', e.target.value)} placeholder="AE" /></label>
        <label><span style={S.lbl}>App version at least</span>
          <input style={S.inp} value={seg.app_version_gte || ''} onChange={(e) => setSeg('app_version_gte', e.target.value.trim())} placeholder="1.0.0" /></label>
        <label><span style={S.lbl}>Has a company</span>
          <select style={S.inp} value={tri('has_company')} onChange={(e) => setTri('has_company', e.target.value)}>
            <option value="">Any</option><option value="yes">Yes</option><option value="no">No</option></select></label>
        <label><span style={S.lbl}>Has paid us</span>
          <select style={S.inp} value={tri('paid')} onChange={(e) => setTri('paid', e.target.value)}>
            <option value="">Any</option><option value="yes">Yes</option><option value="no">No</option></select></label>
        <div />
        <label><span style={S.lbl}>Inactive for at least (days)</span>
          <input style={S.inp} type="number" min={0} value={seg.inactive_days_gte ?? ''} onChange={(e) => num('inactive_days_gte', e.target.value)} /></label>
        <label><span style={S.lbl}>Active in the last (days)</span>
          <input style={S.inp} type="number" min={0} value={seg.active_days_lte ?? ''} onChange={(e) => num('active_days_lte', e.target.value)} /></label>
        <div />
        <label><span style={S.lbl}>Signed up after</span>
          <input style={S.inp} type="date" value={seg.signed_up_after || ''} onChange={(e) => setSeg('signed_up_after', e.target.value || null)} /></label>
        <label><span style={S.lbl}>Signed up before</span>
          <input style={S.inp} type="date" value={seg.signed_up_before || ''} onChange={(e) => setSeg('signed_up_before', e.target.value || null)} /></label>
      </div>
      <span style={{ ...S.lbl, marginTop: 10 }}>Visa application status</span>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {VISA.map((v) => {
          const on = (seg.visa_status || []).includes(v);
          return (
            <button type="button" key={v} onClick={() => setSeg('visa_status', on ? seg.visa_status.filter((x) => x !== v) : [...(seg.visa_status || []), v])}
              style={{ ...S.mini, ...(on ? { background: '#2540D9', color: '#fff', borderColor: '#2540D9' } : {}) }}>{v.replace(/_/g, ' ')}</button>
          );
        })}
      </div>
    </div>
  );
}

function EndPicker({ rec, setRec }) {
  const mode = rec.ends_at ? 'date' : rec.max_sends ? 'count' : 'never';
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select style={{ ...S.inp, width: 170 }} value={mode} onChange={(e) => {
        const m = e.target.value;
        if (m === 'never') setRec({ ends_at: null, max_sends: null });
        if (m === 'date') setRec({ max_sends: null, ends_at: new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10) });
        if (m === 'count') setRec({ ends_at: null, max_sends: 4 });
      }}>
        <option value="never">Never (until paused)</option>
        <option value="date">On a date</option>
        <option value="count">After a number of sends</option>
      </select>
      {mode === 'date' && <input style={{ ...S.inp, width: 170 }} type="date" value={rec.ends_at || ''} onChange={(e) => setRec({ ends_at: e.target.value })} />}
      {mode === 'count' && (
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
          <input style={{ ...S.inp, width: 80 }} type="number" min={1} value={rec.max_sends || ''} onChange={(e) => setRec({ max_sends: e.target.value === '' ? null : Number(e.target.value) })} /> sends
        </label>
      )}
    </div>
  );
}

function TestDialog({ busy, onClose, onSend }) {
  const [text, setText] = useState('');
  return (
    <div style={S.overlay} onClick={() => !busy && onClose()}>
      <div style={{ ...S.modal, width: 'min(460px,100%)' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 6px' }}>Send a test</h3>
        <div style={S.help}>Tests go out right away (quiet hours and limits are ignored) and are not counted in the report. The member needs the app installed with notifications on.</div>
        <button style={{ ...S.btnPrimary, marginTop: 14, width: '100%' }} disabled={busy} onClick={() => onSend({ toMe: true })}>
          {busy ? 'Sending…' : 'Send to me (the member account with my admin email)'}
        </button>
        <div style={{ ...S.lbl, marginTop: 16 }}>Or to a test account (ids or emails, up to 10)</div>
        <div style={{ ...S.help, marginTop: 0, marginBottom: 6 }}>Admins can send tests to themselves and the approved test accounts. Super admins can test on any member.</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={S.inp} value={text} onChange={(e) => setText(e.target.value)} placeholder="117 or dev@onchain.city" />
          <button style={S.btnGhost} disabled={busy || !text.trim()} onClick={() => onSend({ toMe: false, text })}>Send</button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={S.btnGhost} onClick={onClose} disabled={busy}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── iPhone lock-screen preview ──────────────────────────────────────────────
export function PhonePreview({ c }) {
  const title = fillVars(c.title) || 'Title';
  const subtitle = fillVars(c.subtitle);
  const body = fillVars(c.body) || 'Your message shows here.';
  const now = useMemo(() => new Date(), []);
  const clock = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(/\s?[AP]M$/i, '');
  const date = now.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' });
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => { setImgOk(true); }, [c.image_url]);
  const img = c.image_url && imgOk ? c.image_url : null;
  const actions = (c.actions_json || []).map((a) => ACTIONS.find((x) => x.id === a.id)?.title).filter(Boolean);
  const glass = { background: 'rgba(245,245,245,0.78)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 18, color: '#111' };
  const font = { fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif' };
  const icon = (
    <div style={{ width: 22, height: 22, borderRadius: 6, background: '#2540D9', color: '#fff', fontSize: 12, fontWeight: 700, display: 'grid', placeItems: 'center', flexShrink: 0 }}>N</div>
  );
  return (
    <div style={font}>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: '#6B7280', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: 0.3 }}>Lock screen</div>
      <div style={{ width: 320, height: 300, borderRadius: 36, padding: 10, background: '#111', boxShadow: '0 12px 30px rgba(0,0,0,0.25)' }}>
        <div style={{ height: '100%', borderRadius: 28, overflow: 'hidden', background: 'linear-gradient(160deg, #1e3a8a 0%, #6d28d9 55%, #db2777 100%)', padding: '14px 10px', boxSizing: 'border-box', position: 'relative' }}>
          <div style={{ textAlign: 'center', color: '#fff' }}>
            <div style={{ fontSize: 13, opacity: 0.9 }}>{date}</div>
            <div style={{ fontSize: 58, fontWeight: 600, lineHeight: 1.05, letterSpacing: -1 }}>{clock}</div>
          </div>
          <div style={{ ...glass, position: 'absolute', left: 10, right: 10, bottom: 14, padding: '10px 12px', display: 'flex', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 3 }}>
                {icon}<span style={{ fontSize: 11.5, opacity: 0.6, textTransform: 'uppercase', flex: 1 }}>Naytive</span>
                <span style={{ fontSize: 11.5, opacity: 0.55 }}>now</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
              {subtitle && <div style={{ fontSize: 13.5, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtitle}</div>}
              <div style={{ fontSize: 13.5, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{body}</div>
            </div>
            {img && <img src={img} alt="" onError={() => setImgOk(false)} style={{ width: 38, height: 38, borderRadius: 8, objectFit: 'cover', alignSelf: 'center', flexShrink: 0 }} />}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 11.5, fontWeight: 600, color: '#6B7280', margin: '16px 0 6px', textTransform: 'uppercase', letterSpacing: 0.3 }}>Pressed and held</div>
      <div style={{ width: 320, borderRadius: 18, padding: 10, background: '#2b2b30', boxSizing: 'border-box' }}>
        <div style={{ ...glass, overflow: 'hidden', borderRadius: 14 }}>
          {img && <img src={img} alt="" onError={() => setImgOk(false)} style={{ width: '100%', maxHeight: 220, objectFit: 'cover', display: 'block' }} />}
          <div style={{ padding: '10px 12px' }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>{icon}<span style={{ fontSize: 11.5, opacity: 0.6, textTransform: 'uppercase' }}>Naytive</span></div>
            <div style={{ fontSize: 14.5, fontWeight: 600 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 14, fontWeight: 500 }}>{subtitle}</div>}
            <div style={{ fontSize: 14, lineHeight: 1.35, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{body}</div>
          </div>
        </div>
        {actions.length > 0 && (
          <div style={{ ...glass, marginTop: 8, borderRadius: 14, overflow: 'hidden' }}>
            {actions.map((t, i) => (
              <div key={t} style={{ padding: '11px 14px', fontSize: 15, borderTop: i ? '1px solid rgba(0,0,0,0.1)' : 'none' }}>{t}</div>
            ))}
          </div>
        )}
      </div>
      {c.image_url && !imgOk && <div style={{ ...S.help, color: '#B91C1C' }}>The image link did not load. Check it is a public https link.</div>}
      <div style={{ ...S.help, width: 320 }}>Variables show with sample values. Real phones may cut text slightly differently.</div>
    </div>
  );
}
